import { restrictCapitalToAffectedSeries, restrictSociosToNewShares } from './utils/ampliacion-socios.util';
import { completarVnDeAmortizadas, restrictCapitalToReducedSeries, restrictSociosToAmortizedShares } from './utils/reduccion-socios.util';
import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faChevronDown, faChevronUp, faFile, faPencil, faTrash, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CATEGORIAS,
  OperacionSocietaria,
  OperacionCategoria,
  OperacionSubtipo,
  getSeccionesAfectadas,
  getSubtipoConfig,
  getCategoriaConfig,
} from './mock-data/operaciones.mock';
import { ConstitutionReadonlyComponent } from './constitution-readonly/constitution-readonly.component';
import { OperationDetailReadonlyComponent } from './operation-detail-readonly/operation-detail-readonly.component';
import {
  buildOperationDetailSections,
  OperationDetailSection,
} from './utils/operation-detail-display.util';
import { OperationWizardService } from './operation-wizard/operation-wizard.service';
import { CorporateOperationsService } from '../../../core/services/clients/corporate-operations.service';
import { CorporateOperation } from '../../../core/models/corporate-operation.model';
import { ConstitutionSnapshot } from './models/constitution-snapshot.model';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { CodesService } from '../../../core/services/codes/codes.service';
import {
  mapSociosApiToSnapshots,
  mapSociosOperationToSnapshots,
} from '../constitution-form/utils/socio-snapshot.mapper';
import {
  loadAdministrationSnapshotFromApi,
  mapAdministrationToSnapshot,
} from '../constitution-form/utils/admin-snapshot.mapper';
import {
  mapAttorneysApiToSnapshots,
  mapOperationAttorneyToSnapshot,
} from '../constitution-form/utils/attorney-snapshot.mapper';
import { formatEuro } from '../constitution-form/utils/socios-display.util';
import {
  buildHistoricalEstadoActual,
  endOfLocalDay,
  formatDeedDate,
  isDeedOnOrBefore,
  mergeEstadoActualWithOperations,
  operationDeedTime,
  resolveDeedTime,
  sinMiembrosDadosDeBaja,
} from './utils/estado-actual.util';

type SubTab = 'estadoActual' | 'operaciones';

function buildSnapshotFromOperation(op: CorporateOperation, cnaeCodes: any[] = []): ConstitutionSnapshot | null {
  if (!op.operationData) return null;

  const data = op.operationData;
  const fmtEur = (n: number) => formatEuro(n);

  // Escritura from top-level operation fields
  const escritura: ConstitutionSnapshot['escritura'] = {
    fecha: formatDeedDate(op.deedDate),
    concepto: op.deedConcept ?? '',
    notario: op.notary ?? '',
    protocolo: op.protocol ?? '',
    provinciaRM: op.mercantileRegistryProvince ?? '',
    fechaInscripcion: formatDeedDate(op.registrationDate),
    tomo: op.tome ?? '',
    folio: op.folio ?? '',
    hoja: op.sheet ?? '',
    seccion: op.section ?? '',
    libro: op.book ?? '',
    numInscripcion: op.inscriptionNumber ?? '',
  };

  // Capital social
  const cap = data['capitalSocial'] ?? {};
  const hasSeries = !!cap.hasSeries;
  const rawSeries: any[] = Array.isArray(cap.series) ? cap.series : [];
  const series: ConstitutionSnapshot['capitalSocial']['series'] = hasSeries
    ? rawSeries.map((s: any) => ({
        nombre: s.name ?? '',
        id: s.id != null ? Number(s.id) : undefined,
        totalAcciones: s.totalShares ?? 0,
        valorNominal: Number(s.nominalValue) || 0,
        desde: s.sharesNumberFrom ?? 0,
        hasta: s.sharesNumberTo ?? 0,
        capitalSocial: (s.totalShares ?? 0) * (s.nominalValue ?? 0),
      }))
    : [];
  const totalAcciones = hasSeries
    ? series.reduce((sum, s) => sum + s.totalAcciones, 0)
    : (cap.totalShares ?? 0);
  const nominalVal = cap.nominalValue ?? 0;
  const totalCapital = fmtEur(hasSeries
    ? series.reduce((sum, s) => sum + s.capitalSocial, 0)
    : totalAcciones * nominalVal);

  const capitalSnapshot: ConstitutionSnapshot['capitalSocial'] = {
    hasSeries,
    totalCapital,
    totalAcciones,
    acccionesDesde: hasSeries ? undefined : (cap.sharesNumberFrom ?? undefined),
    accionesHasta: hasSeries ? undefined : (cap.sharesNumberTo ?? undefined),
    valorNominal: nominalVal || undefined,
    series,
  };

  const rawSocios: any[] = Array.isArray(data['socios']) ? data['socios'] : [];
  const socios = mapSociosOperationToSnapshots(rawSocios, capitalSnapshot);

  const administracion = mapAdministrationToSnapshot(data['administracion'] ?? {});

  const rawApoderados: any[] = Array.isArray(data['apoderados']) ? data['apoderados'] : [];
  const apoderados = rawApoderados.map(a => mapOperationAttorneyToSnapshot(a));

  const objetoCnae = data['cnaeCode'] ?? '';
  const objetoDescripcion = data['corporateName'] ?? '';

  return {
    escritura,
    objetoSocial: {
      codigoCnae: objetoCnae,
      nombreCnae: cnaeCodes.find((c: any) => c.code === objetoCnae)?.title,
      descripcion: objetoDescripcion,
    },
    capitalSocial: capitalSnapshot,
    socios,
    administracion,
    apoderados,
  };
}

function collectSeriesNamesFromOperationData(
  data: Record<string, unknown> | null | undefined,
  map: Map<number, string>,
): void {
  const cap = data?.['capitalSocial'] as Record<string, unknown> | undefined;
  const series = Array.isArray(cap?.['series']) ? cap['series'] : [];
  for (const s of series) {
    const row = s as Record<string, unknown>;
    const name = row['name'] ?? row['nombre'];
    if (!name) continue;
    const ids = [row['id'], row['feLocalId']].filter(v => v != null && v !== '');
    for (const id of ids) {
      const numId = Number(id);
      if (!Number.isNaN(numId)) map.set(numId, String(name));
    }
  }

  const reducciones = [
    ...(Array.isArray(data?.['reducciones']) ? data['reducciones'] as Record<string, unknown>[] : []),
    data?.['reduccion'] as Record<string, unknown> | undefined,
    data?.['contrasplit'] as Record<string, unknown> | undefined,
  ].filter((row): row is Record<string, unknown> => !!row);
  for (const entry of reducciones) {
    const name = entry['serieNombre'] ?? entry['serieName'];
    if (!name || entry['serie'] == null || entry['serie'] === '') continue;
    const numId = Number(entry['serie']);
    if (!Number.isNaN(numId)) map.set(numId, String(name));
  }

  const namedSeries = [
    { id: data?.['vnSerie'], name: data?.['vnSerieNombre'] },
    { id: data?.['accSerie'], name: data?.['accSerieNombre'] },
  ];
  const ampliacion = data?.['ampliacion'] as Record<string, unknown> | undefined;
  if (ampliacion) {
    namedSeries.push(
      { id: ampliacion['vnSerie'], name: ampliacion['vnSerieNombre'] },
      { id: ampliacion['accSerie'], name: ampliacion['accSerieNombre'] },
    );
  }
  for (const { id, name } of namedSeries) {
    if (!name || id == null || id === '') continue;
    const numId = Number(id);
    if (!Number.isNaN(numId)) map.set(numId, String(name));
  }
}

export function mapToOperacionSocietaria(op: CorporateOperation, cnaeCodes: any[] = []): OperacionSocietaria {
  const categoria = op.categoria as OperacionCategoria;
  const subtipo = (op.subtipo ?? null) as OperacionSubtipo | null;

  const categoriaLabel = CATEGORIAS.find(c => c.value === categoria)?.label ?? categoria;
  const subtipoLabel = subtipo
    ? CATEGORIAS.flatMap(c => c.subtipos).find(s => s.value === subtipo)?.label ?? subtipo
    : '';
  const tipoLabel = subtipoLabel ? `${categoriaLabel}: ${subtipoLabel}` : categoriaLabel;

  const fecha = formatDeedDate(op.deedDate);

  return {
    id: op.id,
    categoria,
    subtipo,
    categoriaLabel,
    subtipoLabel,
    tipoLabel,
    fechaEscritura: fecha,
    notario: op.notary ?? '',
    protocolo: op.protocol ?? '',
    irus: op.irus ?? undefined,
    observaciones: op.observations ?? undefined,
    concepto: op.deedConcept ?? undefined,
    provinciaRM: op.mercantileRegistryProvince ?? undefined,
    fechaInscripcion: formatDeedDate(op.registrationDate) || undefined,
    tomo: op.tome ?? undefined,
    folio: op.folio ?? undefined,
    hoja: op.sheet ?? undefined,
    seccion: op.section ?? undefined,
    libro: op.book ?? undefined,
    numInscripcion: op.inscriptionNumber ?? undefined,
    documentos: op.documents ?? [],
    snapshot: buildSnapshotFromOperation(op, cnaeCodes) as ConstitutionSnapshot,
    seccionesAfectadas: getSeccionesAfectadas(categoria, subtipo),
    operationData: op.operationData ?? null,
    createdAt: op.created_at,
    deedTime: resolveDeedTime(op.deedDate, op.created_at),
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function capitalHasNominal(cap: ConstitutionSnapshot['capitalSocial'] | undefined): boolean {
  if (!cap) return false;
  if (cap.hasSeries) return cap.series.some(s => (s.valorNominal ?? 0) > 0);
  return (cap.valorNominal ?? 0) > 0;
}

@Component({
  selector: 'app-operaciones-societarias-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    DropdownModule,
    ConstitutionReadonlyComponent,
    OperationDetailReadonlyComponent,
  ],
  templateUrl: './operaciones-societarias-form.component.html',
  styleUrl: './operaciones-societarias-form.component.scss',
})
export class OperacionesSocietariasFormComponent implements OnInit, OnChanges {
  @Input() clientId: any;
  @Input() snapshotDate: Date | null = null;

  /** Internal signal mirror of snapshotDate so computed() can track it reactively */
  private readonly _snapshotDate = signal<Date | null>(null);

  private readonly router = inject(Router);
  private readonly wizardService = inject(OperationWizardService);
  private readonly corporateOpsService = inject(CorporateOperationsService);
  private readonly clientsService = inject(ClientsService);
  private readonly codesService = inject(CodesService);

  faPlus = faPlus;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faFile = faFile;
  faPencil = faPencil;
  faTrash = faTrash;
  faTriangleExclamation = faTriangleExclamation;

  activeSubTab = signal<SubTab>('estadoActual');
  operaciones = signal<OperacionSocietaria[]>([]);
  cnaeCodes = signal<any[]>([]);
  expandedId = signal<number | null>(null);
  isLoading = signal(false);
  isLoadingEstadoActual = signal(false);

  estadoActual = signal<ConstitutionSnapshot | null>(null);
  /** Historical estado actual computed from filtered operations when in snapshot mode */
  estadoActualSnapshot = signal<ConstitutionSnapshot | null>(null);
  private readonly clientNamesMap = signal<Map<number, string>>(new Map());
  private readonly seriesNamesMap = signal<Map<number, string>>(new Map());

  /** True when a snapshot date has been selected */
  readonly isSnapshotMode = computed(() => this._snapshotDate() !== null);

  /** Operations filtered by deedDate <= snapshotDate (or all when no date selected) */
  readonly displayedOperaciones = computed(() => {
    const ops = this.operaciones();
    const date = this._snapshotDate();
    if (!date) return ops;
    return ops.filter(op => isDeedOnOrBefore(op, date));
  });

  // ── New operation modal state ───────────────────────────────────────────

  showNewModal = signal(false);
  newCategoria = signal<OperacionCategoria | ''>('');
  newSubtipo = signal<OperacionSubtipo | null>(null);

  // ── Delete modal state ──────────────────────────────────────────────────

  showDeleteModal = signal(false);
  deleteTarget = signal<OperacionSocietaria | null>(null);
  isDeleting = signal(false);

  /** True when a 'constitucion' operation already exists for this client */
  readonly hasConstitucion = computed(() =>
    this.operaciones().some(op => op.categoria === 'constitucion')
  );

  /** ID of the last created operation (by created_at), regardless of deedDate */
  readonly lastCreatedOpId = computed(() => {
    const ops = this.operaciones();
    if (!ops.length) return null;
    return [...ops].reduce((latest, op) =>
      !latest || (op.createdAt && op.createdAt > (latest.createdAt ?? '')) ? op : latest
    ).id;
  });

  /** All categories as dropdown options, disabling 'constitucion' if it already exists */
  readonly categoriaOptions = computed(() =>
    CATEGORIAS.map(c => ({
      label: c.label,
      value: c.value,
      disabled: c.value === 'constitucion' && this.hasConstitucion(),
    }))
  );

  /** Subtypes for the selected category */
  readonly subtipoOptions = computed<{ label: string; value: OperacionSubtipo }[]>(() => {
    const cat = this.newCategoria();
    if (!cat) return [];
    const cfg = CATEGORIAS.find(c => c.value === cat);
    if (!cfg || cfg.subtipos.length === 0) return [];
    return cfg.subtipos.map(s => ({ label: s.label, value: s.value }));
  });

  /** True when the selected category requires a subtype selection (more than one) */
  readonly requiresSubtipo = computed(() => {
    const cat = this.newCategoria();
    if (!cat) return false;
    const cfg = CATEGORIAS.find(c => c.value === cat);
    return !!cfg && cfg.subtipos.length > 1;
  });

  /** True when the "Continuar" button should be enabled */
  readonly canContinue = computed(() => {
    const cat = this.newCategoria();
    if (!cat) return false;
    if (this.requiresSubtipo()) return this.newSubtipo() !== null;
    return true;
  });

  // ── Computed ────────────────────────────────────────────────────────────

  get latestOperacion(): OperacionSocietaria | null {
    const ops = this.operaciones();
    if (!ops.length) return null;
    return [...ops].sort((a, b) => operationDeedTime(b) - operationDeedTime(a))[0];
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['snapshotDate']) {
      // Sync the signal so computed() properties react
      this._snapshotDate.set(this.snapshotDate);

      if (this.snapshotDate) {
        // Rebuild historical estado actual from currently loaded operations
        const filtered = this.displayedOperaciones();
        this.estadoActualSnapshot.set(sinMiembrosDadosDeBaja(buildHistoricalEstadoActual(filtered), endOfLocalDay(this.snapshotDate)));
        // Show "estado actual" tab if there are operations to display
        if (filtered.length > 0) {
          this.activeSubTab.set('estadoActual');
        } else {
          this.activeSubTab.set('operaciones');
        }
      } else {
        // Snapshot cleared → back to live data
        this.estadoActualSnapshot.set(null);
        if (this.operaciones().length > 0) {
          this.activeSubTab.set('estadoActual');
        }
      }
    }
  }

  async ngOnInit(): Promise<void> {
    // Handle wizard result coming back from the route
    const op = this.wizardService.consumeResult();
    if (op) {
      this.expandedId.set(op.id);
      this.activeSubTab.set('estadoActual');
    }

    try {
      const codes = await this.codesService.getCnaeCodes();
      this.cnaeCodes.set(codes ?? []);
    } catch { /* non-critical */ }

    try {
      const names = await this.clientsService.getClientsNames();
      const map = new Map<number, string>();
      (Array.isArray(names) ? names : []).forEach((c: { id?: number; name?: string }) => {
        if (c?.id != null && c.name) map.set(c.id, c.name);
      });
      this.clientNamesMap.set(map);
    } catch { /* non-critical */ }

    await this.loadOperaciones();
    await this.refreshEstadoActual();
  }

  private async refreshEstadoActual(): Promise<void> {
    const apiSnapshot = await this.fetchEstadoActualFromApi();
    if (!apiSnapshot) {
      const opsOnly = buildHistoricalEstadoActual(this.operaciones());
      this.estadoActual.set(sinMiembrosDadosDeBaja(opsOnly));
      return;
    }
    const merged = mergeEstadoActualWithOperations(apiSnapshot, this.operaciones());
    this.estadoActual.set(sinMiembrosDadosDeBaja(merged));
  }

  private async fetchEstadoActualFromApi(): Promise<ConstitutionSnapshot | null> {
    if (!this.clientId) return null;
    this.isLoadingEstadoActual.set(true);
    try {
      const id = Number(this.clientId);
      const [constitution, socialObjectRes, capitalRes, shareholdersRes, administracionRes, attorneysRes] = await Promise.allSettled([
        this.clientsService.getClientConstitution(id),
        this.clientsService.getClientSocialObject(id),
        this.clientsService.getClientSocialCapital(id),
        this.clientsService.getClientShareholders(id),
        loadAdministrationSnapshotFromApi(
          (cid) => this.clientsService.getAdministrationByClient(cid),
          (aid) => this.clientsService.getMembersByAdministration(aid),
          id,
        ),
        this.clientsService.getClientAttorneys(id),
      ]);

      const con = constitution.status === 'fulfilled' ? constitution.value : null;
      const socialObjRaw = socialObjectRes.status === 'fulfilled' ? socialObjectRes.value : null;
      const socialObjList = Array.isArray(socialObjRaw) ? socialObjRaw : (socialObjRaw ? [socialObjRaw] : []);
      const socialObj = socialObjList[socialObjList.length - 1] ?? null;
      const capRaw = capitalRes.status === 'fulfilled' ? capitalRes.value : null;
      const sociosRaw: any[] = shareholdersRes.status === 'fulfilled' ? (shareholdersRes.value ?? []) : [];
      const administracion = administracionRes.status === 'fulfilled'
        ? administracionRes.value
        : { sistema: '', miembros: [] };
      const abogadosRaw: any[] = attorneysRes.status === 'fulfilled' ? (attorneysRes.value ?? []) : [];

      const fmtEur = (n: number) => formatEuro(n);

      // Capital
      const capData = Array.isArray(capRaw) ? capRaw[0] : capRaw;
      const hasSeries = !!capData?.hasSeries;
      const apiSeries: any[] = Array.isArray(capData?.series)
        ? capData.series
        : (Array.isArray(capRaw) && capRaw[0]?.name ? capRaw : []);
      const seriesMap = new Map(this.seriesNamesMap());
      for (const s of apiSeries) {
        const name = s?.name ?? s?.nombre;
        if (s?.id != null && name) seriesMap.set(Number(s.id), name);
      }
      this.seriesNamesMap.set(seriesMap);
      const series: ConstitutionSnapshot['capitalSocial']['series'] = hasSeries
        ? apiSeries.filter((s: any) => s?.name).map((s: any) => ({
            nombre: s.name ?? '',
            id: s.id != null ? Number(s.id) : undefined,
            totalAcciones: s.totalShares ?? 0,
            valorNominal: Number(s.nominalValue) || 0,
            desde: s.sharesNumberFrom ?? 0,
            hasta: s.sharesNumberTo ?? 0,
            capitalSocial: (s.totalShares ?? 0) * (s.nominalValue ?? 0),
          }))
        : [];
      const singleCap = hasSeries ? null : (capData?.socialCapital ?? capData);
      const totalAcciones = hasSeries
        ? series.reduce((sum, s) => sum + s.totalAcciones, 0)
        : (singleCap?.totalShares ?? 0);
      const nominalVal = singleCap?.nominalValue ?? 0;
      const totalCapital = fmtEur(hasSeries
        ? series.reduce((sum, s) => sum + s.capitalSocial, 0)
        : totalAcciones * nominalVal);

      const capitalSnapshot: ConstitutionSnapshot['capitalSocial'] = {
        hasSeries,
        totalCapital,
        totalAcciones,
        acccionesDesde: singleCap?.sharesNumberFrom ?? undefined,
        accionesHasta: singleCap?.sharesNumberTo ?? undefined,
        valorNominal: nominalVal || undefined,
        series,
      };

      const socios = mapSociosApiToSnapshots(sociosRaw, capitalSnapshot);
      const apoderados = mapAttorneysApiToSnapshots(abogadosRaw);

      const snapshot: ConstitutionSnapshot = {
        escritura: {
          fecha: '', concepto: '', notario: '', protocolo: '',
          provinciaRM: '', fechaInscripcion: '', tomo: '', folio: '',
          hoja: '', seccion: '', libro: '', numInscripcion: '',
        },
        objetoSocial: {
          codigoCnae: socialObj?.cnaeCode ?? con?.cnaeCode ?? '',
          nombreCnae: this.cnaeCodes().find((c: any) =>
            c.code === (socialObj?.cnaeCode ?? con?.cnaeCode)
          )?.title,
          descripcion: socialObj?.description ?? con?.corporateName ?? '',
        },
        capitalSocial: capitalSnapshot,
        socios,
        administracion,
        apoderados,
      };

      return snapshot;
    } catch (e) {
      console.error('Error loading estado actual', e);
      return null;
    } finally {
      this.isLoadingEstadoActual.set(false);
    }
  }

  private async loadOperaciones(): Promise<void> {
    if (!this.clientId) return;
    this.isLoading.set(true);
    try {
      const result = await this.corporateOpsService.getByClient(Number(this.clientId));
      const codes = this.cnaeCodes();
      const ops = result.map(op => mapToOperacionSocietaria(op, codes));
      const seriesMap = new Map<number, string>();
      for (const op of result) {
        collectSeriesNamesFromOperationData(op.operationData, seriesMap);
      }
      this.seriesNamesMap.set(seriesMap);
      this.operaciones.set(ops);

      if (this.snapshotDate) {
        // Re-compute historical snapshot after operations are loaded
        const filtered = this.displayedOperaciones();
        this.estadoActualSnapshot.set(buildHistoricalEstadoActual(filtered));
        this.activeSubTab.set(filtered.length > 0 ? 'estadoActual' : 'operaciones');
      } else if (ops.length === 0) {
        this.activeSubTab.set('operaciones');
      }
    } catch (e) {
      console.error('Error loading corporate operations', e);
    } finally {
      this.isLoading.set(false);
    }
  }

  // ── Methods ─────────────────────────────────────────────────────────────

  setSubTab(tab: SubTab) {
    this.activeSubTab.set(tab);
  }

  toggle(id: number) {
    this.expandedId.update(current => (current === id ? null : id));
  }

  isExpanded(id: number) {
    return this.expandedId() === id;
  }

  openNewModal() {
    this.showNewModal.set(true);
  }

  closeNewModal() {
    this.showNewModal.set(false);
    this.newCategoria.set('');
    this.newSubtipo.set(null);
  }

  onCategoriaChange(cat: OperacionCategoria | '') {
    this.newCategoria.set(cat);
    this.newSubtipo.set(null);
    if (cat) {
      const cfg = CATEGORIAS.find(c => c.value === cat);
      if (cfg && cfg.subtipos.length === 1) {
        this.newSubtipo.set(cfg.subtipos[0].value);
      }
    }
  }

  startWizard() {
    if (!this.canContinue()) return;
    const cat = this.newCategoria() as OperacionCategoria;
    const sub = this.newSubtipo();
    this.closeNewModal();
    const queryParams: Record<string, string> = { categoria: cat };
    if (sub) queryParams['subtipo'] = sub;
    this.router.navigate(['/clients/detail', this.clientId, 'nueva-operacion'], { queryParams });
  }

  // ── Edit ────────────────────────────────────────────────────────────────

  editOperacion(event: Event, op: OperacionSocietaria): void {
    event.stopPropagation();
    const queryParams: Record<string, string> = {
      categoria: op.categoria,
      operationId: String(op.id),
    };
    if (op.subtipo) queryParams['subtipo'] = op.subtipo;
    this.router.navigate(['/clients/detail', this.clientId, 'nueva-operacion'], { queryParams });
  }

  // ── Delete ──────────────────────────────────────────────────────────────

  openDeleteModal(event: Event, op: OperacionSocietaria): void {
    event.stopPropagation();
    this.deleteTarget.set(op);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.deleteTarget.set(null);
  }

  async confirmDelete(): Promise<void> {
    const target = this.deleteTarget();
    if (!target || this.isDeleting()) return;
    this.isDeleting.set(true);
    try {
      await this.corporateOpsService.delete(target.id);
      this.closeDeleteModal();
      await this.loadOperaciones();
      await this.refreshEstadoActual();
    } catch (e) {
      console.error('Error deleting corporate operation', e);
    } finally {
      this.isDeleting.set(false);
    }
  }

  save() { /* no-op */ }

  /** The detail of these operations lists only the shares they touch, so the totals must not read as the whole company. */
  sociosParcialLabel(op: OperacionSocietaria): string | null {
    if (op.subtipo === 'reduccion_capital' || op.subtipo === 'escision') return 'Acciones amortizadas';
    if (op.subtipo === 'ampliacion_capital' || op.subtipo === 'operacion_acordeon') {
      const amp = (op.operationData?.['ampliacion'] as Record<string, unknown> | undefined) ?? op.operationData;
      return amp?.['modoAmpliacion'] === 'ampliacion_vn' ? 'Acciones de la serie ampliada' : 'Acciones nuevas';
    }
    return null;
  }

  snapshotForDetail(op: OperacionSocietaria): ConstitutionSnapshot {
    const snap = op.snapshot;
    if (!snap) return snap;
    const base = this.withLiveCapital(op, snap);
    if (op.subtipo === 'ampliacion_capital' || op.subtipo === 'operacion_acordeon') {
      return restrictCapitalToAffectedSeries(restrictSociosToNewShares(base, op.operationData), op.operationData);
    }
    return op.subtipo === 'reduccion_capital' || op.subtipo === 'escision'
      ? restrictCapitalToReducedSeries(
          completarVnDeAmortizadas(restrictSociosToAmortizedShares(base, op.operationData), this.capitalAntesDe(op)),
          op.operationData,
        )
      : base;
  }

  /** Capital que había justo antes de la operación: da el VN de las series que esta dio de baja. */
  private capitalAntesDe(op: OperacionSocietaria): ConstitutionSnapshot['capitalSocial'] | null {
    const t = operationDeedTime(op);
    const anteriores = this.operaciones().filter(o => o.id !== op.id && operationDeedTime(o) < t);
    return buildHistoricalEstadoActual(anteriores)?.capitalSocial ?? null;
  }

  private withLiveCapital(op: OperacionSocietaria, snap: ConstitutionSnapshot): ConstitutionSnapshot {
    if (op.operationData?.['capitalSocial'] != null) return snap;
    const liveCapital = this.estadoActual()?.capitalSocial;
    if (!liveCapital || !capitalHasNominal(liveCapital)) return snap;
    return { ...snap, capitalSocial: liveCapital };
  }

  getOperationDetails(op: OperacionSocietaria): OperationDetailSection[] {
    return buildOperationDetailSections(
      op.categoria,
      op.subtipo,
      op.operationData ?? null,
      {
        resolveClientName: (id) => {
          if (id == null || id === '') return undefined;
          const numId = Number(id);
          return this.clientNamesMap().get(numId) ?? undefined;
        },
        resolveSeriesName: (id) => {
          if (id == null || id === '') return undefined;
          const numId = Number(id);
          if (Number.isNaN(numId)) return undefined;
          return this.seriesNamesMap().get(numId) ?? undefined;
        },
      },
    );
  }

  getTabAfectada(op: OperacionSocietaria): { key: string; label: string; accion: string } | undefined {
    if (op.subtipo) return getSubtipoConfig(op.subtipo)?.tabAfectada;
    return getCategoriaConfig(op.categoria)?.tabAfectada;
  }

  // ── Document actions ─────────────────────────────────────────────────────

  async downloadDocument(operationId: number, fileId: string, fileName: string): Promise<void> {
    try {
      const blob = await this.corporateOpsService.downloadDocument(operationId, fileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading document', e);
    }
  }

  async deleteDocument(operationId: number, fileId: string): Promise<void> {
    try {
      await this.corporateOpsService.deleteDocument(operationId, fileId);
      this.operaciones.update(ops =>
        ops.map(op =>
          op.id === operationId
            ? { ...op, documentos: (op.documentos ?? []).filter(d => d.id !== fileId) }
            : op
        )
      );
    } catch (e) {
      console.error('Error deleting document', e);
    }
  }
}
